import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { OnboardingModal } from "@/components/app/OnboardingModal";
import { DemoTour, isDemoAccount } from "@/components/app/DemoTour";
import {
  LogOut, LayoutDashboard, Users, ClipboardList, Building2, Shield, Target,
  UserCog, Trophy, Settings, CalendarCheck2, CalendarDays, Megaphone, ScrollText, BarChart3,
  Menu, X, CreditCard, Clock, ArrowLeft, HeartPulse, UserCircle, Sun, Moon, Shirt, BookOpen,
} from "lucide-react";

const SUDEPORTE_LOGO = "/brand/sudefootball-white-40.png";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  club_admin: "Admin del Club",
  coordinator: "Coordinador",
  director_deportivo: "Director Deportivo",
  coach: "Entrenador",
  delegado: "Delegado",
};

const CLUB_ADMIN_ROLES = ["club_admin", "coordinator", "director_deportivo"];
const TEAM_STAFF_ROLES = ["coach", "delegado"];

const NAV = {
  super_admin: [
    { to: "/platform", label: "Plataforma", icon: Shield },
    { to: "/platform/clubs", label: "Clubes", icon: Building2 },
  ],
  club_admin: [
    { to: "/club", label: "Panel", icon: LayoutDashboard },
    { to: "/club/categories", label: "Categorías", icon: Trophy },
    { to: "/club/teams", label: "Equipos", icon: Users },
    { to: "/club/players", label: "Jugadores", icon: UserCircle },
    { to: "/club/staff", label: "Equipo Técnico", icon: UserCog },
    { to: "/club/templates", label: "Plantillas de Análisis", icon: Settings },
    { to: "/coach/attendance", label: "Asistencia", icon: CalendarCheck2 },
    { to: "/calendar", label: "Calendario", icon: CalendarDays },
    { to: "/coach/jornadas", label: "Jornadas", icon: ClipboardList },
    { to: "/coach/pizarra", label: "Pizarra", icon: Shirt },
    { to: "/stats", label: "Estadísticas", icon: BarChart3 },
    { to: "/evaluations", label: "Evaluación", icon: ClipboardList },
    { to: "/injuries", label: "Lesiones", icon: HeartPulse },
    { to: "/comms", label: "Comunicación", icon: Megaphone },
    { to: "/guide", label: "Guía de uso", icon: BookOpen },
    { to: "/club/settings", label: "Datos del Club", icon: Settings },
    { to: "/billing", label: "Facturación", icon: CreditCard },
  ],
  coordinator: [
    { to: "/coordinator", label: "Vista Global", icon: LayoutDashboard },
    { to: "/coordinator/teams", label: "Equipos", icon: Users },
    { to: "/club/players", label: "Jugadores", icon: UserCircle },
    { to: "/club/templates", label: "Plantillas de Análisis", icon: Settings },
    { to: "/coach/attendance", label: "Asistencia", icon: CalendarCheck2 },
    { to: "/calendar", label: "Calendario", icon: CalendarDays },
    { to: "/coach/jornadas", label: "Jornadas", icon: ClipboardList },
    { to: "/coach/pizarra", label: "Pizarra", icon: Shirt },
    { to: "/stats", label: "Estadísticas", icon: BarChart3 },
    { to: "/evaluations", label: "Evaluación", icon: ClipboardList },
    { to: "/injuries", label: "Lesiones", icon: HeartPulse },
    { to: "/comms", label: "Comunicación", icon: Megaphone },
    { to: "/guide", label: "Guía de uso", icon: BookOpen },
  ],
  coach: [
    { to: "/coach", label: "Mis Equipos", icon: Target },
    { to: "/coach/jornadas", label: "Jornadas", icon: ClipboardList },
    { to: "/coach/pizarra", label: "Pizarra", icon: Shirt },
    { to: "/club/players", label: "Jugadores", icon: UserCircle },
    { to: "/coach/attendance", label: "Asistencia", icon: CalendarCheck2 },
    { to: "/calendar", label: "Calendario", icon: CalendarDays },
    { to: "/stats", label: "Estadísticas", icon: BarChart3 },
    { to: "/evaluations", label: "Evaluación", icon: ClipboardList },
    { to: "/injuries", label: "Lesiones", icon: HeartPulse },
    { to: "/comms", label: "Comunicación", icon: Megaphone },
    { to: "/guide", label: "Guía de uso", icon: BookOpen },
  ],
};

// director_deportivo shares the coordinator experience; delegado shares the coach one.
NAV.director_deportivo = NAV.coordinator;
NAV.delegado = NAV.coach;

// Plan Familias (B2C): los padres solo necesitan su panel y la facturación.
const NAV_FAMILIA = [
  { to: "/familia", label: "Mi Panel", icon: LayoutDashboard },
  { to: "/guia-player", label: "Guía", icon: BookOpen },
  { to: "/billing", label: "Facturación", icon: CreditCard },
];

const PLAN_LABEL_SHORT = { presencia: "PRESENCIA", club: "CLUB", total: "TOTAL", familia: "PLAYER" };

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("sudeporte_theme") || "dark"; } catch { return "dark"; }
  });
  const isFamilia = user?.club_plan === "familia";
  const nav = isFamilia ? NAV_FAMILIA : (NAV[user?.role] || []);
  const roleLabel = isFamilia ? "Familia" : ROLE_LABEL[user?.role];

  // Guard cruzado familia ↔ club: los padres solo ven /familia y /billing;
  // un club_admin B2B que entre a /familia vuelve a /club.
  useEffect(() => {
    if (!user) return;
    if (isFamilia) {
      const allowed = ["/familia", "/billing"];
      if (!allowed.some((p) => location.pathname.startsWith(p))) navigate("/familia", { replace: true });
    } else if (user.role === "club_admin" && location.pathname.startsWith("/familia")) {
      navigate("/club", { replace: true });
    }
  }, [user, isFamilia, location.pathname, navigate]);

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    try { localStorage.setItem("sudeporte_theme", theme); } catch { /* ignore */ }
  }, [theme]);

  const toggleTheme = () => setTheme(t => (t === "light" ? "dark" : "light"));

  // GHL support chat widget — only for B2B club roles inside the logged-in shell.
  // Familia users get the cheaper native bot (FamiliaBot) instead.
  useEffect(() => {
    const role = user?.role;
    if (!role || role === "super_admin" || role === "platform_sales" || isFamilia) return;
    if (!document.getElementById("ghl-chat-widget-script")) {
      // Dedup: remove any orphan widget instances before (re)injecting the loader.
      document.querySelectorAll("chat-widget").forEach((el) => el.remove());
      const s = document.createElement("script");
      s.id = "ghl-chat-widget-script";
      s.src = "https://widgets.leadconnectorhq.com/loader.js";
      s.setAttribute("data-resources-url", "https://widgets.leadconnectorhq.com/chat-widget/loader.js");
      s.setAttribute("data-widget-id", "6a8dbb32f164fc162246434b");
      document.body.appendChild(s);
    }
    return () => {
      document.getElementById("ghl-chat-widget-script")?.remove();
      document.querySelectorAll("chat-widget").forEach((el) => el.remove());
    };
  }, [user?.role, isFamilia]);

  // Load plan/trial info for club users so the nav can show a real badge.
  useEffect(() => {
    if (!user?.club_id || user.role === "super_admin") return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/clubs/${user.club_id}/trial`);
        if (mounted) setPlanInfo(data);
      } catch (e) { /* silent */ }
    })();
    return () => { mounted = false; };
  }, [user?.club_id, user?.role, location.pathname]);

  // Impersonation banner: when a super_admin jumps into a club, we keep the
  // original super_admin JWT in `super_token_backup` so we can restore it later.
  const impersonating = typeof window !== "undefined" && !!localStorage.getItem("super_token_backup");

  const exitImpersonation = async () => {
    const backup = localStorage.getItem("super_token_backup");
    if (!backup) return;
    localStorage.setItem("access_token", backup);
    localStorage.removeItem("super_token_backup");
    // Force auth context to re-read the token and re-fetch /auth/me
    window.location.href = "/platform";
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <div className="min-h-screen grain flex flex-col relative">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto flex items-center gap-3 md:gap-4 px-4 md:px-6 min-h-16 py-2 md:py-0 md:h-16 flex-wrap md:flex-nowrap">
          {/* Hamburger — full horizontal nav only shows on 2xl (>=1536px)
              because 10 links + brand + right-side controls exceed 1440 width. */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setDrawerOpen(true)}
            className="2xl:hidden w-10 h-10 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
            aria-label="Abrir menú"
          >
            <Menu size={18} />
          </button>

          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 min-w-0">
            <div className="flex flex-col items-start leading-none" data-testid="brand-stack">
              <img src={SUDEPORTE_LOGO} alt="Sudefootball" className="h-7 md:h-8 w-auto object-contain" />
              <span className="font-heading font-black italic tracking-tight leading-none text-foreground text-xs md:text-sm mt-1">sudeporte</span>
            </div>
          </Link>

          <nav className="hidden 2xl:flex items-center gap-1 min-w-0 flex-shrink overflow-x-auto max-w-[820px] xl:max-w-[900px]">
            {nav
              // Hide "Facturación" from the horizontal nav because the plan
              // badge on the right already links to /billing (would overflow).
              .filter(({ to }) => to !== "/billing")
              .map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-${to.replace(/\//g, "-")}`}
                  className={`shrink-0 px-2 h-9 flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 ${
                    isActive(to) ? "text-foreground border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon size={14} />
                  <span className="uppercase tracking-wide font-heading text-[11px] whitespace-nowrap">{label}</span>
                </Link>
              ))}
          </nav>

          <div className="flex-1 min-w-0" />

          {impersonating && (
            <button
              onClick={exitImpersonation}
              data-testid="exit-impersonation-btn"
              className="inline-flex items-center gap-2 h-9 px-3 border-2 border-amber-400 bg-amber-400/10 text-amber-300 text-[11px] font-heading uppercase tracking-widest hover:bg-amber-400/20"
              title="Estás actuando como este club. Vuelve a tu sesión de Super Admin."
            >
              <ArrowLeft size={12} />
              Volver a Super Admin
            </button>
          )}

          {planInfo && (
            <Link
              to="/billing"
              data-testid="plan-badge-nav"
              className={`hidden md:inline-flex items-center gap-2 h-9 px-3 border-2 text-[11px] font-heading uppercase tracking-widest transition-colors ${
                planInfo.is_expired || (planInfo.days_left != null && planInfo.days_left <= 3)
                  ? "border-red-500 text-red-300 bg-red-500/10 hover:bg-red-500/20"
                  : planInfo.is_trial
                    ? (planInfo.days_left <= 7 ? "border-amber-400 text-amber-300 bg-amber-400/10" : "border-blue-500 text-blue-300 bg-blue-500/10")
                    : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
              }`}
              title={planInfo.plan_expires_at ? `Vence el ${planInfo.plan_expires_at}` : ""}
            >
              <Clock size={12}/>
              <span>PLAN {PLAN_LABEL_SHORT[planInfo.plan] || planInfo.plan}</span>
              {planInfo.days_left != null && (
                <span className="opacity-80">·</span>
              )}
              {planInfo.days_left != null && (
                <span>
                  {planInfo.days_left < 0
                    ? "Vencido"
                    : planInfo.is_trial
                      ? `${planInfo.days_left}d prueba`
                      : `${planInfo.days_left}d`}
                </span>
              )}
            </Link>
          )}

          <div className="hidden md:flex items-center gap-3 text-xs">
            <div className="text-right leading-tight">
              <div className="font-semibold text-foreground">{user?.name}</div>
              <div className="overline text-[9px]">{roleLabel}</div>
            </div>
            <div className="w-9 h-9 border border-border flex items-center justify-center font-heading font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>
          {isDemoAccount(user) && (
            <button
              data-testid="demo-tour-btn"
              onClick={() => setTourOpen(true)}
              className="h-10 px-3 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-200 flex items-center gap-2 text-[11px] font-heading uppercase tracking-widest"
              title="Iniciar recorrido guiado de la demo"
            >
              🎬 <span className="hidden md:inline">Modo Demo</span>
            </button>
          )}
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            className="h-10 w-10 border border-border hover:border-primary hover:text-primary transition-colors duration-200 flex items-center justify-center"
            title={theme === "light" ? "Cambiar a modo oscuro" : "Cambiar a modo claro"}
          >
            {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="h-10 w-10 border border-border hover:border-primary hover:text-primary transition-colors duration-200 flex items-center justify-center"
            title="Cerrar sesión"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Drawer — shown whenever the hamburger is available (< 2xl). */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-[60] 2xl:hidden"
          data-testid="mobile-drawer"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-card border-r border-border flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex flex-col items-start leading-none">
                <img src={SUDEPORTE_LOGO} alt="Sudefootball" className="h-7 w-auto object-contain" />
                <span className="font-heading font-black italic tracking-tight leading-none text-foreground text-xs mt-1">sudeporte</span>
              </div>
              <button
                data-testid="mobile-drawer-close"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 border border-border hover:border-primary flex items-center justify-center"
                aria-label="Cerrar menú"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4 border-b border-border">
              <div className="font-semibold text-sm">{user?.name}</div>
              <div className="overline text-[10px]">{roleLabel}</div>
            </div>
            <nav className="flex-1 overflow-y-auto py-2">
              {isDemoAccount(user) && (
                <button
                  data-testid="mobile-demo-tour-btn"
                  onClick={() => { setDrawerOpen(false); setTourOpen(true); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm border-l-2 border-primary text-primary hover:bg-primary/10 transition-colors uppercase tracking-wide font-heading"
                >
                  🎬 Modo Demo
                </button>
              )}
              {nav.map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setDrawerOpen(false)}
                  data-testid={`mobile-nav-${to.replace(/\//g, "-")}`}
                  className={`flex items-center gap-3 px-4 py-3 text-sm border-l-2 transition-colors ${
                    isActive(to) ? "border-primary text-foreground bg-primary/10" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white/[0.03]"
                  }`}
                >
                  <Icon size={16} />
                  <span className="uppercase tracking-wide font-heading">{label}</span>
                </Link>
              ))}
            </nav>
            <button
              onClick={() => { setDrawerOpen(false); handleLogout(); }}
              data-testid="mobile-logout-btn"
              className="m-4 h-11 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider inline-flex items-center justify-center gap-2"
            >
              <LogOut size={14} /> Cerrar sesión
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 md:px-6 py-6 md:py-8 relative z-10">
        {children}
      </main>

      <footer className="border-t border-border py-4 text-center overline text-[10px] flex items-center justify-center gap-2">
        <img src="/brand/sudefootball-white-40.png" alt="Sudefootball" className="h-3.5 w-auto object-contain" />
        <span>by</span>
        <span className="font-heading font-black italic tracking-tight leading-none text-foreground text-[10px]">sudeporte</span>
      </footer>

      {/* Skip onboarding while impersonating so the "Volver a Super Admin"
          control is never covered by a modal from the target club's role. */}
      {!impersonating && !tourOpen && <OnboardingModal role={isFamilia ? "familia" : user?.role} userName={user?.name} />}
      {tourOpen && <DemoTour role={user?.role} onClose={() => setTourOpen(false)} />}
    </div>
  );
}
